import React, { useState, useRef, useEffect, useCallback } from "react";
import { Expand, Volume2, VolumeX } from "lucide-react";
import { Blurhash } from "react-blurhash";

// Default blurhash to use while loading
export const DEFAULT_BLURHASH = "UGF=Hx~VD$NG0Kf4-;x]yY%MD%V@VDM{=|s:";

interface VideoPlayerProps {
  url: string;
  thumbnailUrl: string;
  playing: boolean;
  muted?: boolean;
  onPlay: () => void;
  onMute?: () => void;
  large?: boolean;
  showControls?: boolean;
  title?: string;
  border?: string;
  blurhash?: string;
  preloadMethod?: "auto"| "none"|'preload';
}

const VideoPlayerV3: React.FC<VideoPlayerProps> = ({
  url,
  thumbnailUrl,
  playing,
  muted = false,
  onPlay,
  onMute,
  large = false,
  showControls = false,
  title,
  preloadMethod='none',
  border = "rounded-[12px]",
  blurhash = DEFAULT_BLURHASH,
}) => {
  console.log(blurhash);
  // DOM Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Component State
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isVideoInitialized, setIsVideoInitialized] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(muted ? 0 : 0.5);

  // Initialize video but don't load it until necessary
  useEffect(() => {
    if (videoRef.current && !isVideoInitialized) {
      // Set preload to none initially to prevent automatic loading
      videoRef.current.preload = "none";
      videoRef.current.muted = muted;
      videoRef.current.volume = currentVolume;
      setIsVideoInitialized(true);
    }
  }, [isVideoInitialized, muted, currentVolume]);

  // Control video play/pause based on playing prop
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoInitialized) return;

    if (playing) {
      // If we're supposed to play, start loading the video
      video.preload = "auto";
      // Use promise to handle play success/failure
      video.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    } else if (video.readyState > 0) {
      // Only pause if the video has loaded enough to be paused
      video.pause();
    }
  }, [playing, isVideoInitialized]);

  // Memoized handlers to prevent unnecessary re-renders
  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      console.log("clicked");
      onPlay();
    },
    [onPlay]
  );

  const handleVideoCanPlay = useCallback(() => {
    setIsVideoLoaded(true);
    setIsBuffering(false);
  }, []);

  const handleVideoWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handleVideoPlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleVideoError = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      console.error("Video error:", e);
      setIsBuffering(false);
    },
    []
  );

  const handleMuteToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted;
        setCurrentVolume(videoRef.current.muted ? 0 : 0.5);
        if (onMute) onMute();
      }
    },
    [onMute]
  );

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      const newVolume = parseFloat(e.target.value);
      setCurrentVolume(newVolume);

      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = newVolume === 0;
        if (onMute && videoRef.current.muted !== muted) {
          onMute();
        }
      }
    },
    [muted, onMute]
  );

  const toggleVolumeControl = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowVolumeControl((prev) => !prev);
  }, []);

  // Clean up event listeners and video when component unmounts
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoInitialized) return;

    if (playing) {
      video.preload = "auto";
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    } else {
      video.pause();
    }
  }, [playing, isVideoInitialized]);

  // Helper to determine what should be visible
  const shouldShowThumbnail = !playing || !isVideoLoaded;
  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${border}`}
      onClick={handlePlayClick}
    >
      {/* Blurhash placeholder - always visible until thumbnail loads */}
      {!isThumbnailLoaded && (
        <div className="absolute inset-0 w-full h-full z-10">
          <Blurhash
            hash={blurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
        </div>
      )}

      {/* Thumbnail image - loads immediately, shown when not playing video */}
      <img
        src={thumbnailUrl}
        alt="Video thumbnail"
        className={`absolute scale-125 inset-0 w-full h-full object-cover z-20 transition-opacity duration-300 ${
          isThumbnailLoaded ? "opacity-100" : "opacity-0"
        } ${shouldShowThumbnail ? "visible" : "invisible"}`}
        onLoad={() => setIsThumbnailLoaded(true)}
        loading="eager"
      />

      {/* Video element - lazy loaded on first play */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover z-30 transition-opacity duration-300 ${
          playing && isVideoLoaded ? "opacity-100" : "opacity-0"
        }`}
        playsInline
        preload={preloadMethod}
        autoPlay={playing}
        muted={muted}
        onWaiting={handleVideoWaiting}
        onCanPlay={handleVideoCanPlay}
        onPlaying={handleVideoPlaying}
        onError={handleVideoError}
      >
        <source src={url} type="video/mp4" />
      </video>

      {/* Play button overlay */}
      {shouldShowThumbnail && (
        <button
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40"
          onClick={handlePlayClick}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="24" cy="24" r="24" fill="white" fillOpacity="0.8" />
            <path d="M32 24L20 30.9282L20 17.0718L32 24Z" fill="#333333" />
          </svg>
        </button>
      )}

      {/* Loading spinner */}
      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
        </div>
      )}

      {/* Optional controls */}
      {showControls && (
        <div className="absolute bottom-4 right-4 z-50">
          <div className="flex items-center space-x-2">
            {/* Volume control */}
            <div className="relative">
              <button
                onClick={toggleVolumeControl}
                className="text-white bg-black bg-opacity-20 p-2 rounded-full"
              >
                {currentVolume === 0 ? (
                  <VolumeX size={20} />
                ) : (
                  <Volume2 size={20} />
                )}
              </button>

              {showVolumeControl && (
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={currentVolume}
                    onChange={handleVolumeChange}
                    onClick={(e) => e.stopPropagation()}
                    className="h-20 w-2 bg-white rounded-full appearance-none"
                    style={{
                      WebkitAppearance: "slider-vertical",
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Fullscreen button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (videoRef.current) {
                  videoRef.current.requestFullscreen();
                }
              }}
              className="text-white bg-black bg-opacity-20 p-2 rounded-full"
            >
              <Expand size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Title overlay if provided */}
      {title && (
        <div className="absolute bottom-4 left-4 text-white z-50 bg-black bg-opacity-30 px-2 py-1 rounded">
          {title}
        </div>
      )}
    </div>
  );
};

export default React.memo(VideoPlayerV3);
